import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Build as MaintenanceIcon,
  Cancel as InactiveIcon,
  DirectionsCar as CarIcon,
  Person as DriverIcon,
  Event as DeadlineIcon,
  Add as AddIcon,
  CreditCard as CreditCardIcon,
  MonetizationOn as MonetizationOnIcon,
  PaymentOutlined as PayPalIcon,
  Payments as PaymentIcon,
  PauseCircleOutline as ReserveIcon,
  PlayCircleOutline as ActivateIcon
} from '@mui/icons-material';

import {
  fetchVehicleById,
  deleteVehicle,
  deleteMaintenanceRecord,
  releaseVehicle
} from '../../redux/slices/vehiclesSlice';
import { fetchShifts } from '../../redux/slices/shiftsSlice';
import { isCentralRole, isOperatorAdminRole } from '../../redux/slices/authSlice';
import { getVehicleTypeLabel, getVehicleTypeSeats, getCapabilityLabel } from '../../config/vehicleTypes';
import { getMaintenanceLabel, getMaintenanceType } from '../../config/vehicleMaintenance';
import { getVehicleDeadlines, getVehicleDeadlineWarnings } from '../../utils/vehicleDeadlines';
import { toJsDate } from '../../utils/licenseExpiry';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import EntityDocumentsSection from '../common/EntityDocumentsSection';
import VehicleActiveDialog from './VehicleActiveDialog';
import MaintenanceRecordDialog from './MaintenanceRecordDialog';
import { isVehicleActive } from '../../utils/vehicleActive';

const STATUS_META = {
  active: { label: 'Verfügbar', color: 'success', icon: <ActiveIcon /> },
  in_use: { label: 'Im Einsatz', color: 'primary', icon: <DriverIcon /> },
  maintenance: { label: 'Wartung', color: 'warning', icon: <MaintenanceIcon /> },
  inactive: { label: 'Reserve (deaktiviert)', color: 'default', icon: <InactiveIcon /> }
};

const SHIFT_STATUS_META = {
  completed: { label: 'Abgeschlossen', color: 'success' },
  cancelled: { label: 'Storniert', color: 'error' },
  active: { label: 'Aktiv', color: 'primary' },
  scheduled: { label: 'Geplant', color: 'info' }
};

const PAYMENT_OPTIONS = {
  1: [{ icon: MonetizationOnIcon, label: 'Barzahlung' }],
  2: [
    { icon: MonetizationOnIcon, label: 'Barzahlung' },
    { icon: CreditCardIcon, label: 'EC-/Kreditkartenzahlung' }
  ],
  3: [
    { icon: MonetizationOnIcon, label: 'Barzahlung' },
    { icon: CreditCardIcon, label: 'EC-/Kreditkartenzahlung' },
    { icon: PayPalIcon, label: 'PayPal-Zahlung' }
  ]
};

const formatDate = (value) => {
  const date = toJsDate(value);
  if (!date) return null;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (value) => {
  const date = toJsDate(value);
  if (!date) return null;
  return date.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const formatEuro = (value) => (
  typeof value === 'number' && value > 0
    ? value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
    : null
);

const InfoRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>{children}</Typography>
  </Box>
);

const SectionCard = ({ icon, title, action, children }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="h6" component="h2">{title}</Typography>
        </Box>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} id={`vehicle-tabpanel-${index}`} aria-labelledby={`vehicle-tab-${index}`}>
    {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
  </div>
);

const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentVehicle, isLoading, error } = useSelector(state => state.vehicles);
  const { shifts } = useSelector(state => state.shifts);
  const currentUser = useSelector(state => state.auth?.user);
  const canManage = isCentralRole(currentUser) || isOperatorAdminRole(currentUser);

  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [maintenanceDialog, setMaintenanceDialog] = useState({ open: false, type: 'tuv' });
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [recordDeleteError, setRecordDeleteError] = useState('');
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [activeDialogOpen, setActiveDialogOpen] = useState(false);
  const [documents, setDocuments] = useState([]);

  const vehicle = currentVehicle && currentVehicle.id === id ? currentVehicle : null;

  useEffect(() => {
    if (id) {
      dispatch(fetchVehicleById(id));
      dispatch(fetchShifts({ vehicleId: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    setDocuments(Array.isArray(vehicle?.documents) ? vehicle.documents : []);
  }, [vehicle?.documents]);

  const deadlines = useMemo(() => (vehicle ? getVehicleDeadlines(vehicle) : []), [vehicle]);
  const deadlineWarnings = useMemo(() => (vehicle ? getVehicleDeadlineWarnings(vehicle) : []), [vehicle]);
  const vehicleShifts = useMemo(
    () => (shifts || []).filter(shift => shift.vehicle_id === id || shift.vehicle?.id === id),
    [shifts, id]
  );
  const maintenanceHistory = useMemo(() => (
    [...(vehicle?.maintenanceHistory || [])].sort((a, b) => (
      (toJsDate(b.date)?.getTime() || 0) - (toJsDate(a.date)?.getTime() || 0)
    ))
  ), [vehicle?.maintenanceHistory]);

  const openMaintenanceDialog = (type = 'tuv') => setMaintenanceDialog({ open: true, type });

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    dispatch(deleteVehicle(id)).then((result) => {
      if (!result.error) navigate('/fahrzeuge');
    });
  };

  const handleRecordDeleteConfirm = async () => {
    const record = recordToDelete;
    setRecordToDelete(null);
    setRecordDeleteError('');
    const result = await dispatch(deleteMaintenanceRecord({ vehicleId: id, recordId: record.id }));
    if (result.error) {
      setRecordDeleteError(result.payload || 'Der Eintrag konnte nicht gelöscht werden.');
    }
  };

  const handleReleaseConfirm = () => {
    dispatch(releaseVehicle(id));
    setReleaseDialogOpen(false);
  };

  // Ladeanzeige nur, solange noch kein Fahrzeug da ist: Nachladen nach einer
  // Wartung soll die Seite (und den offenen Tab) nicht ausblenden.
  if (!vehicle && isLoading) {
    return <Loading message="Fahrzeug wird geladen..." />;
  }

  if (!vehicle && error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden des Fahrzeugs"
        message={error}
        onRetry={() => dispatch(fetchVehicleById(id))}
      />
    );
  }

  if (!vehicle) {
    return (
      <ErrorMessage
        title="Fahrzeug nicht gefunden"
        message="Das angeforderte Fahrzeug konnte nicht gefunden werden."
        onRetry={() => navigate('/fahrzeuge')}
      />
    );
  }

  const active = isVehicleActive(vehicle);
  const statusMeta = STATUS_META[vehicle.status] || STATUS_META.inactive;
  const driver = vehicle.current_driver;
  const driverName = driver?.user?.name || driver?.name || 'Unbekannt';
  const paymentOptions = PAYMENT_OPTIONS[vehicle.paymentMethods] || [];
  const subtitle = [
    vehicle.vehicleNumber ? `Nr. ${vehicle.vehicleNumber}` : null,
    vehicle.licensePlate,
    `${getVehicleTypeLabel(vehicle.vehicleType)} · ${getVehicleTypeSeats(vehicle.vehicleType)} Sitzplätze`
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Fahrzeugdetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrzeuge', path: '/fahrzeuge' },
          { label: vehicle.model || 'Fahrzeug', path: `/fahrzeuge/${id}` }
        ]}
        actionButton={canManage ? {
          label: 'Bearbeiten',
          icon: <EditIcon />,
          path: `/fahrzeuge/${id}/bearbeiten`
        } : null}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!active && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Dieses Fahrzeug ist als Reserve deaktiviert: Es ist in der Fahrer-App nicht wählbar und
          belegt keinen Platz im Fahrzeugkontingent.
        </Alert>
      )}

      {deadlineWarnings.length > 0 && (
        <Alert
          severity={deadlineWarnings.some(d => d.status.color === 'error') ? 'error' : 'warning'}
          sx={{ mb: 2 }}
          action={canManage ? (
            <Button color="inherit" size="small" onClick={() => openMaintenanceDialog(deadlineWarnings[0].key)}>
              Wartung eintragen
            </Button>
          ) : null}
        >
          {deadlineWarnings.map(d => (
            d.status.days < 0
              ? `${d.short} seit ${formatDate(d.date)} überfällig`
              : `${d.short} fällig am ${formatDate(d.date)}`
          )).join(' · ')}
        </Alert>
      )}

      {/* Kopf: Fahrzeug + Aktionen */}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <CarIcon sx={{ fontSize: { xs: 48, md: 64 }, color: 'primary.main', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5" component="h1" sx={{ overflowWrap: 'anywhere' }}>
                  {vehicle.model || 'Unbekanntes Fahrzeug'}
                </Typography>
                <Chip
                  label={statusMeta.label}
                  color={statusMeta.color}
                  icon={statusMeta.icon}
                  size="small"
                />
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            </Box>
          </Box>

          {canManage && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
              {vehicle.inUse && driver && (
                <Button variant="contained" onClick={() => setReleaseDialogOpen(true)}>
                  Fahrzeug freigeben
                </Button>
              )}
              <Button
                variant="outlined"
                color={active ? 'warning' : 'success'}
                startIcon={active ? <ReserveIcon /> : <ActivateIcon />}
                onClick={() => setActiveDialogOpen(true)}
              >
                {active ? 'Als Reserve deaktivieren' : 'Aktivieren'}
              </Button>
              <Tooltip title={vehicle.inUse ? 'Fahrzeug ist im Einsatz' : ''}>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={vehicle.inUse}
                  >
                    Löschen
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Box>
      </Paper>

      {/* Übersicht */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SectionCard icon={<CarIcon color="primary" />} title="Fahrzeugdaten">
            <InfoRow label="Fahrzeugnummer">{vehicle.vehicleNumber || 'Nicht vergeben'}</InfoRow>
            <InfoRow label="Kennzeichen">{vehicle.licensePlate || '–'}</InfoRow>
            <InfoRow label="Ordnungsnummer">{vehicle.ordnungsnummer || 'Nicht hinterlegt'}</InfoRow>
            <InfoRow label="Typ">{getVehicleTypeLabel(vehicle.vehicleType)}</InfoRow>
            <InfoRow label="Sitzplätze">{getVehicleTypeSeats(vehicle.vehicleType)}</InfoRow>
            {vehicle.phoneNumber && <InfoRow label="Telefon">{vehicle.phoneNumber}</InfoRow>}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SectionCard icon={<PaymentIcon color="primary" />} title="Fähigkeiten & Zahlung">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              Spezialfähigkeiten
            </Typography>
            {vehicle.specialCapabilities?.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {vehicle.specialCapabilities.map(cap => (
                  <Chip key={cap} label={getCapabilityLabel(cap)} size="small" variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>Keine</Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              Zahlungsmöglichkeiten
            </Typography>
            {paymentOptions.length > 0 ? (
              <Stack spacing={0.75}>
                {paymentOptions.map(({ icon: Icon, label }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon color="primary" fontSize="small" />
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2">Nicht hinterlegt</Typography>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SectionCard
            icon={<DeadlineIcon color="primary" />}
            title="Fristen"
            action={canManage ? (
              <Tooltip title="Wartung eintragen">
                <IconButton size="small" onClick={() => openMaintenanceDialog()} aria-label="Wartung eintragen">
                  <AddIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          >
            {deadlines.map(deadline => (
              <Box key={deadline.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{deadline.short}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {deadline.date ? formatDate(deadline.date) : 'Nicht hinterlegt'}
                  </Typography>
                  {deadline.status && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={deadline.status.color}
                      label={deadline.status.days < 0 ? 'überfällig' : deadline.status.days === 0 ? 'heute' : `${deadline.status.days} T`}
                      sx={{ height: 22 }}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SectionCard icon={<DriverIcon color="primary" />} title="Fahrer & Aktivität">
            {driver ? (
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                startIcon={<DriverIcon />}
                component={Link}
                to={`/fahrer/${driver.id}`}
                sx={{ mb: 1 }}
              >
                {driverName}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Kein Fahrer zugewiesen
              </Typography>
            )}
            {driver && (
              <InfoRow label="Status">{driver.isOnline ? 'Online' : 'Offline'}</InfoRow>
            )}
            {driver && (driver.phone || driver.user?.phone) && (
              <InfoRow label="Telefon">{driver.phone || driver.user?.phone}</InfoRow>
            )}
            <InfoRow label="Angelegt am">{formatDate(vehicle.createdAt) || 'Unbekannt'}</InfoRow>
            <InfoRow label="Zuletzt aktiv">{formatDateTime(vehicle.lastActivity) || 'Unbekannt'}</InfoRow>
            <InfoRow label="Schichten">{vehicleShifts.length}</InfoRow>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="inherit"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', '& .Mui-selected': { fontWeight: 600 } }}
        >
          <Tab label={`Wartungshistorie (${maintenanceHistory.length})`} id="vehicle-tab-0" />
          <Tab label={`Dokumente (${documents.length})`} id="vehicle-tab-1" />
          <Tab label="Schichten" id="vehicle-tab-2" />
        </Tabs>

        {/* Wartungshistorie */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6">Wartungshistorie</Typography>
            {canManage && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openMaintenanceDialog()}>
                Wartung eintragen
              </Button>
            )}
          </Box>

          {recordDeleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRecordDeleteError('')}>
              {recordDeleteError}
            </Alert>
          )}

          {maintenanceHistory.length > 0 ? (
            <Stack spacing={1.5}>
              {maintenanceHistory.map((record, index) => {
                const type = getMaintenanceType(record.type);
                const cost = formatEuro(record.cost);
                const details = [
                  record.odometer ? `${Number(record.odometer).toLocaleString('de-DE')} km` : null,
                  cost
                ].filter(Boolean).join(' · ');
                return (
                  <Paper key={record.id || index} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                            {getMaintenanceLabel(record)}
                          </Typography>
                          {type?.key === 'sonstiges' && (
                            <Chip label="Sonstiges" size="small" variant="outlined" sx={{ height: 22 }} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Durchgeführt am {formatDate(record.date) || 'unbekannt'}
                          {record.nextDueDate ? ` · nächste Fälligkeit ${formatDate(record.nextDueDate)}` : ''}
                        </Typography>
                        {details && (
                          <Typography variant="body2" color="text.secondary">{details}</Typography>
                        )}
                        {record.notes && (
                          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                            {record.notes}
                          </Typography>
                        )}
                      </Box>
                      {canManage && record.id && (
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Wartungseintrag löschen"
                          onClick={() => setRecordToDelete(record)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
              Noch keine Wartung eingetragen.
            </Typography>
          )}
        </TabPanel>

        {/* Dokumente */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>Fahrzeugdokumente</Typography>
          <EntityDocumentsSection
            kind="vehicle"
            entityId={id}
            documents={documents}
            onDocumentsChange={setDocuments}
            inputId="vehicle-details-document-upload"
            readOnly={!canManage}
          />
        </TabPanel>

        {/* Schichten */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>Schichten mit diesem Fahrzeug</Typography>
          {vehicleShifts.length > 0 ? (
            <Stack spacing={1.5}>
              {vehicleShifts.slice(0, 5).map((shift) => {
                const shiftMeta = SHIFT_STATUS_META[shift.status] || { label: shift.status || 'Unbekannt', color: 'default' };
                return (
                  <Paper key={shift.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="subtitle1">
                        {formatDateTime(shift.start_time) || 'Unbekannt'} – {formatDateTime(shift.end_time) || 'offen'}
                      </Typography>
                      <Chip label={shiftMeta.label} color={shiftMeta.color} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        Fahrer: {shift.driver ? (
                          <Link to={`/fahrer/${shift.driver.id}`}>{shift.driver.user?.name || 'Unbekannt'}</Link>
                        ) : 'Kein Fahrer'}
                      </Typography>
                      <Typography variant="body2">
                        Fahrten: {shift.total_rides || 0} · Einnahmen: {formatEuro(shift.total_earnings) || '0,00 €'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button variant="outlined" size="small" color="inherit" component={Link} to={`/schichten/${shift.id}`}>
                        Details anzeigen
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
              Keine Schichten für dieses Fahrzeug gefunden.
            </Typography>
          )}
          {vehicleShifts.length > 5 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" component={Link} to={`/schichten?vehicleId=${id}`}>
                Alle Schichten anzeigen
              </Button>
            </Box>
          )}
        </TabPanel>

      </Paper>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Fahrzeug löschen"
        content={`Möchten Sie das Fahrzeug ${vehicle.model} (${vehicle.licensePlate}) wirklich löschen? Hinterlegte Dokumente werden ebenfalls gelöscht. Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <DeleteConfirmDialog
        open={!!recordToDelete}
        title="Wartungseintrag löschen"
        content={recordToDelete
          ? `„${getMaintenanceLabel(recordToDelete)}" vom ${formatDate(recordToDelete.date) || 'unbekannten Datum'} wird gelöscht.`
          : ''}
        onConfirm={handleRecordDeleteConfirm}
        onCancel={() => setRecordToDelete(null)}
      />

      <VehicleActiveDialog
        vehicle={vehicle}
        open={activeDialogOpen}
        onClose={() => setActiveDialogOpen(false)}
      />

      <MaintenanceRecordDialog
        open={maintenanceDialog.open}
        initialType={maintenanceDialog.type}
        vehicle={vehicle}
        onClose={() => setMaintenanceDialog(prev => ({ ...prev, open: false }))}
      />

      <Dialog open={releaseDialogOpen} onClose={() => setReleaseDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Fahrzeug freigeben</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Möchten Sie das Fahrzeug {vehicle.model} ({vehicle.licensePlate}) wirklich freigeben?
          </Typography>
          <Typography variant="body2" color="error">
            Hinweis: Der Fahrer {driverName} wird dadurch von diesem Fahrzeug abgemeldet.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setReleaseDialogOpen(false)} color="inherit">Abbrechen</Button>
          <Button onClick={handleReleaseConfirm} variant="contained">
            Fahrzeug freigeben
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleDetails;
